import { useCallback, useMemo } from "react"
import { useAnalytics } from "connected-components/analytics"
import type { SeriesSelection } from "renderers/multi-line-graph/hooks/use-series-selection"
import { GRAPH_EVENTS } from "../analytics/events"
import { selectItemsWithData } from "../helpers"

export interface AddSeriesMetricItem {
  id: string
  label: string
}

export interface AddSeriesMetricGroup {
  id: string
  label?: string
  items: AddSeriesMetricItem[]
}

interface UseGraphAddSeriesParams {
  /** The template's full family metric list, grouped by category; null/undefined hides the "+ Add series" control (Beta has no resolvable metric family). */
  availableMetricsGroups: AddSeriesMetricGroup[] | null | undefined
  /** Ids currently plotted by the built-in selection — distinct from addedMetrics. */
  visibleItemIds: string[]
  /** Every id native to the CURRENT view's built-in family, enabled or disabled — scopes "restoring a hidden built-in" to this view only. */
  builtInItemIds: string[]
  addedMetrics: string[]
  addMetric: (metricId: string) => void
  disabledItems: Record<string, boolean | undefined>
  setDisabledItems: (itemId: string, defaultDisabled?: boolean) => void
  setOnlyItem: (itemId: string, allItemIds: string[]) => void
  seriesColors: string[]
  seriesColorSlots: Record<string, number>
  setSeriesColorSlots: (seriesColorSlots: Record<string, number>) => void
}

// Shared add-series wiring for the five built-in templates with "+ Add series": analytics and stable series colors.
export function useGraphAddSeries({
  availableMetricsGroups,
  visibleItemIds,
  builtInItemIds,
  addedMetrics,
  addMetric,
  disabledItems,
  setDisabledItems,
  setOnlyItem,
  seriesColors,
  seriesColorSlots,
  setSeriesColorSlots,
}: UseGraphAddSeriesParams) {
  const analytics = useAnalytics()

  const alreadyShownMetricIds = useMemo(
    () => new Set([...visibleItemIds, ...addedMetrics]),
    [visibleItemIds, addedMetrics],
  )

  const onAddSeries = useCallback((metricId: string) => {
    const isAlreadyListed = builtInItemIds.includes(metricId) || addedMetrics.includes(metricId)
    if (isAlreadyListed) {
      if (disabledItems[metricId] !== false) setDisabledItems(metricId, true)
      return
    }
    const wasModified = addedMetrics.length > 0
    addMetric(metricId)
    analytics.track(GRAPH_EVENTS.SERIES_ADDED, { metricId })
    if (!wasModified) {
      analytics.track(GRAPH_EVENTS.VIEW_MODIFIED, {})
    }
  }, [addedMetrics, addMetric, analytics, builtInItemIds, disabledItems, setDisabledItems])

  // Hiding a built-in (not added) series has no dedicated event; only the add-series surface is instrumented here.
  const trackedSetDisabledItems = useCallback((itemId: string, defaultDisabled?: boolean) => {
    const wasVisible = !disabledItems[itemId]
    if (addedMetrics.includes(itemId) && wasVisible) {
      analytics.track(GRAPH_EVENTS.SERIES_HIDDEN, { metricId: itemId })
    }
    setDisabledItems(itemId, defaultDisabled)
  }, [addedMetrics, disabledItems, setDisabledItems, analytics])

  // "Only" hides other rows directly, so an added series hidden that way needs its own tracking; "All" only un-hides.
  const trackedSetOnlyItem = useCallback((itemId: string, allItemIds: string[]) => {
    allItemIds.forEach((id) => {
      if (id === itemId) return
      const wasVisible = !disabledItems[id]
      if (addedMetrics.includes(id) && wasVisible) {
        analytics.track(GRAPH_EVENTS.SERIES_HIDDEN, { metricId: id })
      }
    })
    setOnlyItem(itemId, allItemIds)
  }, [addedMetrics, disabledItems, setOnlyItem, analytics])

  const filterItems = useCallback(
    <Item extends { id: string }>(items: Item[], data: unknown[]): Item[] =>
      selectItemsWithData(items, data, seriesColors, disabledItems, seriesColorSlots, addedMetrics),
    [seriesColors, disabledItems, seriesColorSlots, addedMetrics],
  )

  const seriesSelection = useMemo<SeriesSelection>(() => ({
    pinColorSlots: setSeriesColorSlots,
  }), [setSeriesColorSlots])

  return useMemo(() => ({
    addSeriesMetrics: availableMetricsGroups ?? undefined,
    alreadyShownMetricIds,
    onAddSeries,
    trackedSetDisabledItems,
    trackedSetOnlyItem,
    filterItems,
    seriesSelection,
  }), [availableMetricsGroups, alreadyShownMetricIds, onAddSeries, trackedSetDisabledItems, trackedSetOnlyItem, filterItems, seriesSelection])
}
